import React, { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';

import Text from '~/components/core/TextComponents/Text';
import TextFieldFormik from '~/components/TextFieldFormik';
import Radio from '~/components/ui/atoms/Radio';
import RadioGroup from '~/components/ui/atoms/RadioGroup';
import formatDaysAsWeeksAndDays from '~/Utils/formatDaysAsWeekAndDays';

import { INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED } from '../consts';
import TEXTS from '../texts';
import type { SettingsFormInitialValuesType } from '../types';

import { canTreatInitialDaysValueAsWeeks } from './incomeProtectionSettingsFormHelpers';

const BENEFIT_PERIOD_INPUT_TYPES = {
  DAYS: 'DAYS',
  WEEKS: 'WEEKS',
} as const;

type BenefitPeriodInputType = keyof typeof BENEFIT_PERIOD_INPUT_TYPES;

const isBenefitPeriodInputType = (value: string): value is BenefitPeriodInputType =>
  Object.hasOwn(BENEFIT_PERIOD_INPUT_TYPES, value);

const BENEFIT_PERIOD_INTERNAL_KEY = 'benefit_period_internal';

const IncomeProtectionBenefitPeriodRow = () => {
  const { setFieldValue, values, initialValues, handleChange } = useFormikContext<SettingsFormInitialValuesType>();
  const [benefitPeriodInputType, setBenefitPeriodInputType] = useState<BenefitPeriodInputType>(() => {
    return canTreatInitialDaysValueAsWeeks(initialValues.benefit_period_num_of_days)
      ? BENEFIT_PERIOD_INPUT_TYPES.WEEKS
      : BENEFIT_PERIOD_INPUT_TYPES.DAYS;
  });
  const currentInternalValue = values.benefit_period_internal;

  const handleBenefitPeriodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(event);

      if (event.target.value === '') {
        setFieldValue(INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.benefit_period_num_of_days, null);
      } else {
        const internalValueAsNumber = Number(event.target.value);
        const nextValue =
          benefitPeriodInputType === BENEFIT_PERIOD_INPUT_TYPES.WEEKS
            ? internalValueAsNumber * 7
            : internalValueAsNumber;
        setFieldValue(INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.benefit_period_num_of_days, nextValue);
      }
    },
    [setFieldValue, benefitPeriodInputType, handleChange]
  );

  const handleInputTypeChange = useCallback(
    (inputType: BenefitPeriodInputType) => {
      setBenefitPeriodInputType(inputType);
      if (typeof currentInternalValue === 'number') {
        setFieldValue(
          INCOME_PROTECTION_SETTING_CREATION_KEYS_MAPPED.benefit_period_num_of_days,
          inputType === BENEFIT_PERIOD_INPUT_TYPES.WEEKS ? currentInternalValue * 7 : currentInternalValue
        );
      }
    },
    [setFieldValue, currentInternalValue]
  );

  const helperText =
    typeof values.benefit_period_num_of_days === 'number'
      ? formatDaysAsWeeksAndDays(values.benefit_period_num_of_days, formatDaysAsWeeksAndDays.AND_SEPARATOR)
      : ' '; // ensures no layout shift

  return (
    <>
      <TextFieldFormik
        id={BENEFIT_PERIOD_INTERNAL_KEY}
        label={TEXTS.settingFormLabels.benefit_period_num_of_days}
        InputProps={{ inputProps: { type: 'number', inputMode: 'numeric', min: 0 } }}
        // @ts-expect-error - TextFieldFormik is not yet typed
        onChange={handleBenefitPeriodChange}
        helperText={helperText}
      />
      {/* The group wraps the Text paragraph, which cannot hold a div. As a one-cell grid it stretches
          the paragraph over the whole cell of the form's grid, as when the paragraph was the cell. */}
      <RadioGroup
        className="grid"
        aria-label={TEXTS.benefitPeriodUnit}
        value={benefitPeriodInputType}
        onChange={(_event, value) => {
          if (isBenefitPeriodInputType(value)) {
            handleInputTypeChange(value);
          }
        }}
      >
        <Text className="flex gap-16" variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
          <label className="flex items-center">
            <Radio value={BENEFIT_PERIOD_INPUT_TYPES.WEEKS} />
            {TEXTS.inWeeks}
          </label>
          <label className="flex items-center">
            <Radio value={BENEFIT_PERIOD_INPUT_TYPES.DAYS} />
            {TEXTS.inDays}
          </label>
        </Text>
      </RadioGroup>
    </>
  );
};

export default IncomeProtectionBenefitPeriodRow;
